import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function MyStaff({ playerId }) {
  const [approvedStaff, setApprovedStaff] = useState([]);
  const [pendingStaff, setPendingStaff] = useState([]);
  const [deniedStaff, setDeniedStaff] = useState([]);
  const [otherStaff, setOtherStaff] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/staff/player/${playerId}`, {
          headers: { 'player-id': playerId }
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Failed to fetch staff');
        const data = await res.json();
        if (!Array.isArray(data)) throw new Error('Invalid data format');

        setApprovedStaff(data.filter(s => s.status === 'Approved'));
        setPendingStaff(data.filter(s => s.status === 'Pending'));
        setDeniedStaff(data.filter(s => s.status === 'Denied'));
        setOtherStaff(data.filter(s => !['Approved', 'Pending', 'Denied'].includes(s.status)));
      } catch (error) {
        setMessage('Failed to fetch staff: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    if (playerId) fetchStaff();
  }, [playerId]);

  const dismissDeniedStaff = async (staffId, staffName) => {
    const staff = deniedStaff.find(s => s._id === staffId);
    if (!staff || staff.status !== 'Denied') {
      setMessage('Cannot dismiss: Staff is not denied.');
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to permanently delete ${staffName}? This cannot be undone.`);
    if (!confirmed) {
      setMessage('Dismissal cancelled.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/staff/${staffId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        },
        body: JSON.stringify({ playerId })
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to delete staff');
      const data = await res.json();
      setMessage(data.message);
      setDeniedStaff(prev => prev.filter(s => s._id !== staffId));
    } catch (error) {
      setMessage(`Failed to delete staff: ${error.message}`);
    }
  };

  const claimAllRewards = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/claim-all-profession-rewards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        },
        body: JSON.stringify({ playerId })
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to claim rewards');
      const data = await res.json();
      setMessage(`Collected ${data.totalIncome} DFG$ from all professions`);
    } catch (error) {
      setMessage(`Failed to claim rewards: ${error.message}`);
    }
  };

  if (loading) {
    return <p style={{ textAlign: 'center', color: '#888', paddingTop: '60px' }}>Loading...</p>;
  }

  const staffContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px'
  };

  const staffCardStyle = {
    padding: '15px',
    backgroundColor: '#fff',
    borderRadius: '5px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s',
    textDecoration: 'none',
    color: 'inherit',
    textAlign: 'center'
  };

  const staffCardHoverStyle = {
    transform: 'scale(1.02)'
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', paddingTop: '60px' }}>
      <h1 style={{ color: '#1E88E5', textAlign: 'center', marginBottom: '20px', fontSize: '1.8em' }}>My Staff</h1>
      {message && (
        <div style={{
          backgroundColor: message.includes('Failed') ? '#EF5350' : '#4CAF50',
          padding: '10px',
          borderRadius: '5px',
          textAlign: 'center',
          color: 'white',
          fontWeight: 'bold',
          marginBottom: '20px'
        }}>
          {message}
        </div>
      )}

      {/* Approved Staff */}
      <div style={{
        backgroundColor: '#f9f9f9',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        border: '1px solid #d9ead3',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Approved Staff</h2>
        <button
          onClick={claimAllRewards}
          style={{
            padding: '5px 10px',
            backgroundColor: '#1E88E5',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '15px',
            fontWeight: 'bold'
          }}
          onMouseOver={e => e.target.style.backgroundColor = '#1565C0'}
          onMouseOut={e => e.target.style.backgroundColor = '#1E88E5'}
        >
          Collect All Profession Income
        </button>
        {approvedStaff.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#888', fontSize: '16px' }}>No approved staff yet.</p>
        ) : (
          <div style={staffContainerStyle}>
            {approvedStaff.map(staff => (
              <div
                key={staff._id}
                style={staffCardStyle}
                onMouseOver={e => e.currentTarget.style.transform = staffCardHoverStyle.transform}
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <Link to={`/staff/${staff._id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  {staff.imageLink && (
                    <img
                      src={staff.imageLink}
                      alt={staff.staffName}
                      style={{
                        maxWidth: '100px',
                        maxHeight: '100px',
                        objectFit: 'contain',
                        borderRadius: '5px',
                        marginBottom: '10px'
                      }}
                    />
                  )}
                  <h3 style={{ color: '#4CAF50', margin: '0 0 10px', fontSize: '1.1em' }}>{staff.staffName}</h3>
                </Link>
                {staff.profession?.skillName ? (
                  <p style={{ color: '#333', fontSize: '14px', margin: '0' }}>
                    {staff.profession.skillName} (Level {staff.profession.level}): {staff.profession.dailyIncome} DFG$/day
                  </p>
                ) : (
                  <p style={{ color: '#888', fontSize: '14px', margin: '0' }}>No profession set</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Staff */}
      <div style={{
        backgroundColor: '#f9f9f9',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        border: '1px solid #d9ead3',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Pending Staff</h2>
        {pendingStaff.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#888', fontSize: '16px' }}>No pending staff registrations.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: '0', color: '#333', fontSize: '16px' }}>
            {pendingStaff.map(staff => (
              <li key={staff._id} style={{ marginBottom: '10px' }}>
                {staff.staffName} (Pending Approval)
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Denied Staff */}
      <div style={{
        backgroundColor: '#f9f9f9',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        border: '1px solid #d9ead3',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Denied Staff</h2>
        {deniedStaff.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#888', fontSize: '16px' }}>No denied staff.</p>
        ) : (
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {deniedStaff.map(staff => (
              <div key={staff._id} style={{
                padding: '15px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                marginBottom: '15px',
                border: '1px solid #BBDEFB'
              }}>
                <p style={{ fontSize: '16px', color: '#333' }}>
                  {staff.staffName} - <span style={{ color: '#EF5350' }}>Denied</span>
                </p>
                {staff.denyReason && (
                  <p style={{ color: '#888', margin: '5px 0' }}>
                    Reason: {staff.denyReason}
                  </p>
                )}
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={() => dismissDeniedStaff(staff._id, staff.staffName)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#1E88E5',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseOver={e => e.target.style.backgroundColor = '#1976D2'}
                    onMouseOut={e => e.target.style.backgroundColor = '#1E88E5'}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Other Staff (Debugging) */}
      <div style={{
        backgroundColor: '#f9f9f9',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        border: '1px solid #d9ead3',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Other Staff (Debug)</h2>
        {otherStaff.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#888', fontSize: '16px' }}>No staff with unexpected status.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: '0', color: '#333', fontSize: '16px' }}>
            {otherStaff.map(staff => (
              <li key={staff._id} style={{ marginBottom: '10px' }}>
                {staff.staffName} (Status: {staff.status || 'None'})
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default MyStaff;
